interface ExtraColorProps {
  light: ExtraModeColorProps;
  dark: ExtraModeColorProps;
}

interface ExtraModeColorProps {
  // _docs
  docsLayoutContentBg: string;
  docsLayoutHeaderBg: string;
  docsComponentsColor: string;
  docsSectionBg: string;

  // action
  actionActive: string;
  actionHover: string;
  actionSelected: string;
  actionFocus: string;
  actionDisabled: string;
  actionDisabledBg: string;

  // background
  backgroundDefault: string;
  backgroundPaper: string;
  backgroundNeutral: string;

  // components
  componentsDivider: string;
  componentsBackdrop: string;

  // components / button
  componentsButtonOutlined: string;

  // components / paper
  componentsPaperOutlined: string;
  componentsPaperBgBlur: string;

  // components / textfield
  componentsTextfieldFilled: string;
  componentsTextfieldUnderline: string;
  componentsTextfieldOutline: string;

  // components / tooltip
  componentsTooltipColor: string;
  componentsTooltipBg: string;

  // dark <-> light
  darkLightPrimary: string;
  darkLightSecondary: string;
  darkLightInfo: string;
  darkLightSuccess: string;
  darkLightWarning: string;
  darkLightError: string;

  // darker <-> lighter
  darkerLighterPrimary: string;
  darkerLighterSecondary: string;
  darkerLighterInfo: string;
  darkerLighterSuccess: string;
  darkerLighterWarning: string;
  darkerLighterError: string;

  // global / default
  globalDefaultColor: string;
  globalDefaultBg: string;
  globalDefaultBgHovered: string;

  // global / inherit
  globalInheritColor: string;
  globalInheritBg: string;
  globalInheritBgHovered: string;

  // lighter <-> darker
  lighterDarkerPrimary: string;
  lighterDarkerSecondary: string;
  lighterDarkerInfo: string;
  lighterDarkerSuccess: string;
  lighterDarkerWarning: string;
  lighterDarkerError: string;

  // main <-> light
  mainLightPrimary: string;
  mainLightSecondary: string;
  mainLightInfo: string;
  mainLightSuccess: string;
  mainLightWarning: string;
  mainLightError: string;

  // text
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
}

declare module '@mui/material/styles' {
  interface Theme {
    extraColors: ExtraColorProps;
  }
  interface ThemeOptions {
    extraColors?: ExtraColorProps;
  }
}

export const extraColor = {
  divider: 'rgba(145 158 171 / 0.2)',
  actionHover: 'rgba(145 158 171 / 0.08)',
  actionSelected: 'rgba(145 158 171 / 0.16)',
  actionFocus: 'rgba(145 158 171 / 0.24)',
  actionDisabled: 'rgba(145 158 171 / 0.8)',
  actionDisabledBackground: 'rgba(145 158 171 / 0.24)',
  actionHoverOpacity: '0.08',
  actionDisabledOpacity: '0.48',
  actionActive: '#637381',
  actionSelectedOpacity: '0.08',
  actionFocusOpacity: '0.12',
  actionActivatedOpacity: '0.12',
  textPrimary: '#1C252E',
  textSecondary: '#637381',
  textDisabled: '#919EAB',
  alertErrorColor: 'rgb(102, 68, 52)',
  alertInfoColor: 'rgb(38, 97, 97)',
  alertSuccessColor: 'rgb(47, 94, 55)',
  alertWarningColor: 'rgb(102, 85, 40)',
  alertErrorFilledBg: 'var(--palette-error-main, #FF5630)',
  alertInfoFilledBg: 'var(--palette-info-main, #00B8D9)',
  alertSuccessFilledBg: 'var(--palette-success-main, #22C55E)',
  alertWarningFilledBg: 'var(--palette-warning-main, #FFAB00)',
  alertErrorFilledColor: '#fff',
  alertInfoFilledColor: 'rgba(0, 0, 0, 0.87)',
  alertSuccessFilledColor: 'rgba(0, 0, 0, 0.87)',
  alertWarningFilledColor: 'rgba(0, 0, 0, 0.87)',
  alertErrorStandardBg: 'rgb(255, 246, 242)',
  alertInfoStandardBg: 'rgb(239, 253, 253)',
  alertSuccessStandardBg: 'rgb(241, 253, 243)',
  alertWarningStandardBg: 'rgb(255, 250, 239)',
  alertErrorIconColor: 'var(--palette-error-main, #FF5630)',
  alertInfoIconColor: 'var(--palette-info-main, #00B8D9)',
  alertSuccessIconColor: 'var(--palette-success-main, #22C55E)',
  alertWarningIconColor: 'var(--palette-warning-main, #FFAB00)',
  appBarDefaultBg: 'var(--palette-grey-100, #F9FAFB)',
  avatarDefaultBg: 'var(--palette-grey-400, #C4CDD5)',
  buttonInheritContainedBg: 'var(--palette-grey-300, #DFE3E8)',
  buttonInheritContainedHoverBg: 'var(--palette-grey-A100, #f5f5f5)',
  chipDefaultBorder: 'var(--palette-grey-400, #C4CDD5)',
  chipDefaultAvatarColor: 'var(--palette-grey-700, #454F5B)',
  chipDefaultIconColor: 'var(--palette-grey-700, #454F5B)',
  filledInputBg: 'rgba(0, 0, 0, 0.06)',
  filledInputHoverBg: 'rgba(0, 0, 0, 0.09)',
  filledInputDisabledBg: 'rgba(0, 0, 0, 0.12)',
  linearProgressPrimaryBg: 'rgb(253, 190, 168)',
  linearProgressSecondaryBg: 'rgb(212, 177, 255)',
  linearProgressErrorBg: 'rgb(255, 190, 176)',
  linearProgressInfoBg: 'rgb(158, 228, 240)',
  linearProgressSuccessBg: 'rgb(171, 232, 193)',
  linearProgressWarningBg: 'rgb(255, 223, 158)',
  skeletonBg: 'rgba(var(--palette-text-primaryChannel, 28 37 46) / 0.11)',
  sliderPrimaryTrack: 'rgb(253, 190, 168)',
  sliderSecondaryTrack: 'rgb(212, 177, 255)',
  sliderErrorTrack: 'rgb(255, 190, 176)',
  sliderInfoTrack: 'rgb(158, 228, 240)',
  sliderSuccessTrack: 'rgb(171, 232, 193)',
  sliderWarningTrack: 'rgb(255, 223, 158)',
  snackbarContentBg: 'rgb(50, 50, 50)',
  snackbarContentColor: '#fff',
  speedDialActionFabHoverBg: 'rgb(216, 216, 216)',
  stepConnectorBorder: 'var(--palette-grey-400, #C4CDD5)',
  stepContentBorder: 'var(--palette-grey-400, #C4CDD5)',
  switchDefaultColor: 'var(--palette-common-white, #FFFFFF)',
  switchDefaultDisabledColor: 'var(--palette-grey-100, #F9FAFB)',
  switchPrimaryDisabledColor: 'rgb(253, 190, 168)',
  switchSecondaryDisabledColor: 'rgb(212, 177, 255)',
  switchErrorDisabledColor: 'rgb(255, 190, 176)',
  switchInfoDisabledColor: 'rgb(158, 228, 240)',
  switchSuccessDisabledColor: 'rgb(171, 232, 193)',
  switchWarningDisabledColor: 'rgb(255, 223, 158)',
  tooltipBg: 'rgba(69, 79, 91, 0.92)',

  // other
  otherFacebook: '#1877f2',
  otherTwitter: '#00aaec',
  otherLinkedin: '#007ebb',
  otherInstagram: '#e02d69',
  otherSkin: '#fdbab9',
  otherMask: 'rgba(255, 255, 255, 0)',

  light: {
    // _docs
    docsLayoutContentBg: 'var(--grey-100)',
    docsLayoutHeaderBg: 'var(--white-main)',
    docsComponentsColor: '#7b61ff',
    docsSectionBg: 'var(--grey-900)',

    // action
    actionActive: 'var(--grey-600)',
    actionHover: 'var(--grey-8)',
    actionSelected: 'var(--grey-16)',
    actionFocus: 'var(--grey-24)',
    actionDisabled: 'rgba(145, 158, 171, 0.8)',
    actionDisabledBg: 'var(--grey-20)',

    // background
    backgroundDefault: 'var(--white-main)',
    backgroundPaper: 'var(--white-main)',
    backgroundNeutral: 'var(--grey-200)',

    // components
    componentsDivider: 'rgba(145, 158, 171, 0.2)',
    componentsBackdrop: 'rgba(22, 28, 36, 0.8)',

    // components / button
    componentsButtonOutlined: 'var(--grey-32)',

    // components / paper
    componentsPaperOutlined: 'var(--grey-16)',
    componentsPaperBgBlur: 'rgba(255, 255, 255, 0.9)',

    // components / textfield
    componentsTextfieldFilled: 'var(--grey-8)',
    componentsTextfieldUnderline: 'var(--grey-32)',
    componentsTextfieldOutline: 'var(--grey-20)',

    // components / tooltip
    componentsTooltipColor: 'var(--white-main)',
    componentsTooltipBg: 'var(--grey-800)',

    // dark <-> light
    darkLightPrimary: 'var(--primary-dark)',
    darkLightSecondary: 'var(--secondary-dark)',
    darkLightInfo: 'var(--info-dark)',
    darkLightSuccess: 'var(--black-success-dark)',
    darkLightWarning: 'var(--warning-dark)',
    darkLightError: 'var(--error-dark)',

    // darker <-> lighter
    darkerLighterPrimary: 'var(--primary-darker)',
    darkerLighterSecondary: 'var(--secondary-darker)',
    darkerLighterInfo: 'var(--info-darker)',
    darkerLighterSuccess: 'var(--black-success-darker)',
    darkerLighterWarning: 'var(--warning-darker)',
    darkerLighterError: 'var(--error-darker)',

    // global / default
    globalDefaultColor: 'var(--grey-800)',
    globalDefaultBg: 'var(--grey-300)',
    globalDefaultBgHovered: 'var(--grey-400)',

    // global / inherit
    globalInheritColor: 'var(--white-main)',
    globalInheritBg: 'var(--grey-800)',
    globalInheritBgHovered: 'var(--grey-700)',

    // lighter <-> darker
    lighterDarkerPrimary: 'var(--primary-lighter)',
    lighterDarkerSecondary: 'var(--secondary-lighter)',
    lighterDarkerInfo: 'var(--info-lighter)',
    lighterDarkerSuccess: 'var(--black-success-lighter)',
    lighterDarkerWarning: 'var(--warning-lighter)',
    lighterDarkerError: 'var(--error-lighter)',

    // main <-> light
    mainLightPrimary: 'var(--primary-main)',
    mainLightSecondary: 'var(--secondary-main)',
    mainLightInfo: 'var(--info-main)',
    mainLightSuccess: 'var(--black-success-main)',
    mainLightWarning: 'var(--warning-main)',
    mainLightError: 'var(--error-main)',

    // text
    textPrimary: 'var(--grey-800)',
    textSecondary: 'var(--grey-600)',
    textDisabled: 'var(--grey-500)',
  },

  dark: {
    // _docs
    docsLayoutContentBg: 'var(--grey-900)',
    docsLayoutHeaderBg: 'var(--grey-8)',
    docsComponentsColor: '#7b61ff',
    docsSectionBg: 'var(--grey-900)',

    // action
    actionActive: 'var(--grey-500)',
    actionHover: 'var(--grey-8)',
    actionSelected: 'var(--grey-16)',
    actionFocus: 'var(--grey-24)',
    actionDisabled: 'rgba(145, 158, 171, 0.8)',
    actionDisabledBg: 'var(--grey-20)',

    // background
    backgroundDefault: 'var(--grey-900)',
    backgroundPaper: 'var(--grey-800)',
    backgroundNeutral: 'var(--grey-12)',

    // components
    componentsDivider: 'rgba(145, 158, 171, 0.2)',
    componentsBackdrop: 'rgba(22, 28, 36, 0.8)',

    // components / button
    componentsButtonOutlined: 'var(--grey-32)',

    // components / paper
    componentsPaperOutlined: 'var(--grey-16)',
    componentsPaperBgBlur: 'rgba(33, 43, 54, 0.9)',

    // components / textfield
    componentsTextfieldFilled: 'var(--grey-8)',
    componentsTextfieldUnderline: 'var(--grey-32)',
    componentsTextfieldOutline: 'var(--grey-20)',

    // components / tooltip
    componentsTooltipColor: 'var(--white-main)',
    componentsTooltipBg: 'var(--grey-700)',

    // dark <-> light
    darkLightPrimary: 'var(--primary-light)',
    darkLightSecondary: 'var(--secondary-light)',
    darkLightInfo: 'var(--info-light)',
    darkLightSuccess: 'var(--black-success-light)',
    darkLightWarning: 'var(--warning-light)',
    darkLightError: 'var(--error-light)',

    // darker <-> lighter
    darkerLighterPrimary: 'var(--primary-lighter)',
    darkerLighterSecondary: 'var(--secondary-lighter)',
    darkerLighterInfo: 'var(--info-lighter)',
    darkerLighterSuccess: 'var(--black-success-lighter)',
    darkerLighterWarning: 'var(--warning-lighter)',
    darkerLighterError: 'var(--error-lighter)',

    // global / default
    globalDefaultColor: 'var(--grey-800)',
    globalDefaultBg: 'var(--grey-300)',
    globalDefaultBgHovered: 'var(--grey-400)',

    // global / inherit
    globalInheritColor: 'var(--grey-800)',
    globalInheritBg: 'var(--white-main)',
    globalInheritBgHovered: 'var(--grey-400)',

    // lighter <-> darker
    lighterDarkerPrimary: 'var(--primary-darker)',
    lighterDarkerSecondary: 'var(--secondary-darker)',
    lighterDarkerInfo: 'var(--info-darker)',
    lighterDarkerSuccess: 'var(--black-success-darker)',
    lighterDarkerWarning: 'var(--warning-darker)',
    lighterDarkerError: 'var(--error-darker)',

    // main <-> light
    mainLightPrimary: 'var(--primary-light)',
    mainLightSecondary: 'var(--secondary-light)',
    mainLightInfo: 'var(--info-light)',
    mainLightSuccess: 'var(--black-success-light)',
    mainLightWarning: 'var(--warning-light)',
    mainLightError: 'var(--error-light)',

    // text
    textPrimary: 'var(--white-main)',
    textSecondary: 'var(--grey-500)',
    textDisabled: 'var(--grey-600)',
  },
};
